import {
  Button,
  CardBackground,
  Dialog,
  Typography,
} from "@hellocarbo/lasagne";
import { Check, Edit2, Info } from "lucide-react";
import { use } from "react";
import { Trans, useTranslation } from "react-i18next";
import invariant from "tiny-invariant";

import { PortalContainerContext } from "@/contexts";
import { TrajectoryActionPlanContext } from "@/pages/Trajectory/TrajectoryActionPlan";
import { TTrajectoryAction } from "@/types";

import ActionListItem from "./ActionListItem";
import ProgressBar from "./ProgressBar";

type ActionsListProps = {
  isInEdition?: boolean;
  onEditionChange: () => void;
  actions: Array<TTrajectoryAction>;
};

function ActionsList({
  isInEdition = false,
  onEditionChange,
  actions,
}: ActionsListProps) {
  const { t, i18n } = useTranslation(["formatter", "trajectory"]);
  const { container } = use(PortalContainerContext);
  const {
    scenarioProjections,
    reducedTrajectoryPercentage,
    reducedSbtiPercentage,
  } = use(TrajectoryActionPlanContext);

  invariant(
    scenarioProjections &&
      typeof reducedTrajectoryPercentage !== "undefined" &&
      typeof reducedSbtiPercentage !== "undefined",

    "Trajectory data is missing"
  );

  const lastProjectionYear = Object.keys(scenarioProjections.normal)
    .map((year) => Number(year))
    .sort()
    .at(-1);

  const progressPercentage =
    reducedSbtiPercentage && reducedSbtiPercentage != 0
      ? (100 * reducedTrajectoryPercentage) / reducedSbtiPercentage
      : 0;

  return (
    <CardBackground>
      <div className="mb-sm flex items-center justify-between">
        <Typography as="h2" step="2" weight="bold">
          {t("trajectory:actionPlan.actionPlan")}
        </Typography>
        <Button
          shape="soft"
          intent="secondary"
          size="small"
          onClick={onEditionChange}
          PrefixIcon={isInEdition ? Check : Edit2}
          disabled={isInEdition && !actions.length}
        >
          {t(`common:${isInEdition ? "validate" : "modify"}`)}
        </Button>
      </div>
      {isInEdition && (
        <div className="border-grayscale-xx-light py-xs px-md mb-xs rounded-md border">
          <Typography as="h3" step="-1" weight="bold">
            {t("trajectory:reduction.reductionBy", {
              year: lastProjectionYear,
            })}
          </Typography>
          <div className="my-3xs gap-x-2xs flex items-center">
            <ProgressBar progress={progressPercentage} />
            <Typography step="-1" weight="semibold" className="text-grayscale">
              {t("formatter:percentage", {
                value: reducedTrajectoryPercentage / 100,
              })}
            </Typography>
          </div>
          <div className="py-3xs gap-x-2xs flex items-center">
            <Dialog
              portalContainer={container as Element}
              trigger={
                <button data-testid="dialog-trigger">
                  <Info className="text-grayscale-dark size-sm" />
                </button>
              }
              title={t("trajectory:reduction.dialog.title")}
              size="small"
            >
              <Typography className="text-grayscale-dark">
                <Trans
                  i18nKey="trajectory:reduction.dialog.description"
                  components={{ 1: <br /> }}
                />
              </Typography>
            </Dialog>
            <Typography step="-1" className="text-grayscale">
              <Trans
                i18nKey="trajectory:reduction.sbtiTarget"
                values={{
                  percentage: t("formatter:percentage", {
                    value: reducedSbtiPercentage / 100,
                  }),
                }}
                components={{ 1: <span className="font-bold" /> }}
              />
            </Typography>
          </div>
        </div>
      )}
      {actions.length ? (
        <ul className="gap-y-xs flex flex-col">
          {actions.map((action) => {
            const name =
              i18n.language === "en" ? action.name_en : action.name_fr;
            const content =
              i18n.language === "en" ? action.content_en : action.content_fr;

            const {
              id: actionId,
              category,
              examples,
              status,
              reduction_factor,
              recommended,
              difficulty,
              reduction_tco2e,
              intensity_percentage,
              is_intensity_editable,
            } = action;

            return (
              <ActionListItem
                key={actionId}
                name={name}
                category={category}
                examples={examples}
                status={status}
                content={content}
                reduction_factor={reduction_factor}
                recommended={recommended}
                difficulty={difficulty}
                reduction_tco2e={reduction_tco2e}
                intensity_percentage={intensity_percentage}
                isReadOnly={!isInEdition}
                actionId={actionId}
                intensityIsEditable={is_intensity_editable}
              />
            );
          })}
        </ul>
      ) : (
        <div className="border-grayscale-xx-light p-sm rounded-lg border text-center">
          <Typography step="-1" className="text-grayscale">
            {t("trajectory:actionPlan.addReductionActions")}
          </Typography>
        </div>
      )}
    </CardBackground>
  );
}

export default ActionsList;
